import React, { useEffect, useState } from "react";
import { X, Star, MapPin, Briefcase, Globe, Award } from "lucide-react";
import api from "@/lib/api";
import ProfilePhoto from "@/components/ProfilePhoto";
import { CandidateBadges, CompatScore } from "@/components/CandidateBadges";
import { categoryLabel } from "@/lib/constants";

/**
 * Compare 2 or 3 candidates side-by-side (spec §5).
 * Props:
 *  - open: boolean
 *  - candidateIds: string[] (2..3)
 *  - missionId?: string — enables contextual compatibility score
 *  - onClose: () => void
 */
export default function ComparisonModal({ open, candidateIds = [], missionId, onClose }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || candidateIds.length < 2) return;
        setLoading(true);
        setError("");
        api.post("/client/candidates/compare", {
            candidate_user_ids: candidateIds,
            mission_id: missionId,
        })
            .then((r) => setRows(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Erreur"))
            .finally(() => setLoading(false));
    }, [open, candidateIds.join(","), missionId]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-auto py-8 px-4"
            onClick={onClose}
            data-testid="comparison-modal"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center px-6 py-5 border-b border-slate-200">
                    <div>
                        <div className="overline mb-1">Comparaison</div>
                        <h2 className="font-display text-2xl text-[#0B192C]">
                            {rows.length} candidat{rows.length > 1 ? "s" : ""} côte-à-côte
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
                        data-testid="comparison-close"
                        aria-label="Fermer"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6">
                    {loading && (
                        <div className="text-center py-12 text-slate-400 text-sm">Chargement…</div>
                    )}
                    {error && (
                        <div className="text-center py-6 text-sm text-red-600">{error}</div>
                    )}
                    {!loading && !error && rows.length === 0 && (
                        <div className="text-center py-12 text-slate-400 text-sm">
                            Aucun candidat comparable pour l'instant.
                        </div>
                    )}

                    {!loading && rows.length > 0 && (
                        <div className={`grid gap-4 ${rows.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                            {rows.map((c) => (
                                <div
                                    key={c.user_id}
                                    className="sd-card p-5 flex flex-col"
                                    data-testid={`compare-card-${c.user_id}`}
                                >
                                    <div className="flex items-center gap-3 mb-4">
                                        {c.has_portrait && (
                                            <ProfilePhoto candidateUserId={c.user_id} size={52} />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="font-display text-lg text-[#0B192C] truncate">
                                                {c.first_name} {c.last_name}
                                            </div>
                                            <div className="text-xs text-slate-500 flex items-center gap-1">
                                                <MapPin size={11} /> {c.city || "—"}
                                            </div>
                                        </div>
                                        {c.compatibility_score != null && (
                                            <CompatScore
                                                score={c.compatibility_score}
                                                testId={`compare-score-${c.user_id}`}
                                            />
                                        )}
                                    </div>

                                    <div className="space-y-2.5 text-sm">
                                        <Row icon={Briefcase} label="Métier">
                                            <span className="font-semibold text-[#0B192C]">
                                                {categoryLabel(c.primary_job) || "—"}
                                            </span>
                                            {(c.secondary_jobs || []).length > 0 && (
                                                <span className="text-xs text-slate-500 block">
                                                    + {c.secondary_jobs.length} autre{c.secondary_jobs.length > 1 ? "s" : ""}
                                                </span>
                                            )}
                                        </Row>
                                        <Row icon={Award} label="Expérience">
                                            <b>{c.experience_years} an{c.experience_years > 1 ? "s" : ""}</b>
                                        </Row>
                                        {c.distance_km != null && (
                                            <Row icon={MapPin} label="Distance">
                                                <b>{c.distance_km.toFixed(1)} km</b>
                                            </Row>
                                        )}
                                        <Row icon={Star} label="Note">
                                            {c.rating != null && c.rating_count > 0 ? (
                                                <b className="text-[#F5A623]">
                                                    ★ {Number(c.rating).toFixed(1)}
                                                    <span className="text-slate-400 font-normal ml-1">
                                                        ({c.rating_count})
                                                    </span>
                                                </b>
                                            ) : (
                                                <span className="text-slate-400">Aucune évaluation</span>
                                            )}
                                        </Row>
                                        <Row icon={Briefcase} label="Missions réalisées">
                                            <b>{c.missions_count ?? 0}</b>
                                        </Row>
                                        <Row icon={Globe} label="Langues">
                                            {(c.languages || []).length > 0 ? (
                                                <span className="text-xs">
                                                    {c.languages.map((l, i) => (
                                                        <span key={i}>
                                                            {(l.lang || l) + (l.level ? ` (${l.level})` : "")}
                                                            {i < c.languages.length - 1 ? ", " : ""}
                                                        </span>
                                                    ))}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </Row>
                                        {c.presence_rate != null && (
                                            <Row icon={Award} label="Taux de présence">
                                                <b>{Math.round(c.presence_rate * 100)}%</b>
                                            </Row>
                                        )}
                                    </div>

                                    <div className="mt-4 pt-4 border-t border-slate-100">
                                        <div className="overline mb-2 text-[10px]">Vérifications</div>
                                        <CandidateBadges badges={c.badges || []} size="xs" />
                                        {(c.badges || []).length === 0 && (
                                            <div className="text-[11px] text-slate-400 italic">
                                                Vérifications en cours
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Row({ icon: Icon, label, children }) {
    return (
        <div className="flex items-start gap-2 leading-tight">
            <Icon size={13} className="text-slate-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                    {label}
                </div>
                <div className="text-[13px] text-slate-700">{children}</div>
            </div>
        </div>
    );
}
